import { Fragment, type MouseEventHandler } from 'react';
import { Tab } from '@headlessui/react';
import { Button } from 'components/Button';
import { Alert } from 'components/Alert';
import {
  sendHistoryStrings as strings,
  summaryManagementStrings as summary,
} from 'constants/strings';
import type { TSendHistoryDetail } from 'types/sendHistory';
import MessagePreview from '../SendMessageForAll/MessagePreview';
import { buildSummaryPreviewData } from '../SummaryManagement/summaryPreview';
import {
  formatSummaryClickRate,
  formatSummaryCount,
} from '../SummaryManagement/summaryDisplay';
import { formatSendHistoryDate } from './format';

export function SendHistoryDetail({
  detail,
  onClickedUsers,
}: {
  detail: TSendHistoryDetail;
  onClickedUsers: MouseEventHandler<HTMLButtonElement>;
}) {
  const preview = buildSummaryPreviewData(detail.preview.sections);
  return (
    <div className="grid min-w-0 gap-4">
      <dl className="grid grid-cols-2 gap-3 rounded-2xl bg-surface/60 p-4 text-sm sm:grid-cols-4">
        <Metric
          label={summary.table.startedAt}
          value={formatSendHistoryDate(detail.startedAt)}
        />
        <Metric
          label={summary.table.sourceType}
          value={summary.sourceTypes[detail.sourceType]}
        />
        <Metric
          label={summary.table.status}
          value={summary.deliveryStatus[detail.status]}
        />
        <Metric
          label={summary.table.sentRecipientCount}
          value={formatSummaryCount(detail.sentRecipientCount)}
        />
      </dl>
      {detail.sourceLabel && (
        <p className="break-words text-sm font-semibold text-ink">
          {detail.sourceLabel}
        </p>
      )}
      <p className="text-xs text-muted">{strings.sentHint}</p>
      <Tab.Group>
        <Tab.List aria-label={strings.detailTitle} className="flex gap-2">
          {[strings.contentTab, strings.clicksTab].map((label) => (
            <Tab key={label} as={Fragment}>
              {({ selected }) => (
                <Button
                  type="button"
                  variant={selected ? 'primary' : 'outline'}
                >
                  {label}
                </Button>
              )}
            </Tab>
          ))}
        </Tab.List>
        {detail.incompleteReason && <Alert>{detail.incompleteReason}</Alert>}
        <Tab.Panels>
          <Tab.Panel className="grid gap-3">
            <p className="text-xs text-muted">{strings.snapshotHint}</p>
            {detail.preview.unavailableReason && (
              <Alert>{detail.preview.unavailableReason}</Alert>
            )}
            {preview.sections.length > 0 && (
              <div
                className="mx-auto flex h-[540px] w-full max-w-[340px] flex-col overflow-hidden rounded-[2rem] border-8 border-white bg-white shadow-soft"
                onClickCapture={(event) => {
                  const target = event.target;
                  if (target instanceof Element && target.closest('a')) {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }}
              >
                <MessagePreview
                  sections={preview.sections}
                  assets={preview.assets}
                  buttonLabel={detail.preview.buttonLabel}
                  chatLinkCta={detail.preview.chatLinkCta}
                  historical
                />
              </div>
            )}
          </Tab.Panel>
          <Tab.Panel className="grid gap-4">
            {detail.coverage === 'NOT_APPLICABLE' ? (
              <p className="text-sm text-muted">{strings.noTrackingUrl}</p>
            ) : (
              <>
                {detail.coverage !== 'FULL' && (
                  <Alert>{summary.coverageWarnings[detail.coverage]}</Alert>
                )}
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <Metric
                    label={summary.table.totalClicks}
                    value={formatSummaryCount(detail.totalClicks)}
                  />
                  <Metric
                    label={summary.table.uniqueClickRecipientCount}
                    value={formatSummaryCount(detail.uniqueClickRecipientCount)}
                  />
                  <Metric
                    label={strings.clickEligibleRecipients}
                    value={formatSummaryCount(
                      detail.clickEligibleRecipientCount,
                    )}
                  />
                  <Metric
                    label={summary.table.clickRate}
                    value={formatSummaryClickRate(detail.clickRate)}
                  />
                </dl>
                <p className="text-xs text-muted">{strings.clickRateHint}</p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClickedUsers}
                  disabled={!detail.recipientTrackingSupported}
                >
                  {summary.clickedUsers.title}
                </Button>
                {!detail.recipientTrackingSupported && (
                  <p className="text-xs text-muted">
                    {strings.recipientTrackingUnavailable}
                  </p>
                )}
                <ul className="grid gap-3">
                  {detail.links.map((link) => (
                    <li
                      key={link.id}
                      className="rounded-xl border border-border p-3 text-sm"
                    >
                      <p className="break-all">{link.displayUrl}</p>
                      <p className="mt-2 text-xs text-muted">
                        {summary.table.totalClicks}:{' '}
                        {formatSummaryCount(link.totalClicks)} /{' '}
                        {summary.table.uniqueClickRecipientCount}:{' '}
                        {formatSummaryCount(link.uniqueClickRecipientCount)}
                      </p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted">{label}</dt>
      <dd className="font-semibold text-ink">{value}</dd>
    </div>
  );
}
